import { NavLink } from 'react-router-dom';
import { Logo } from './Logo';
import { UserMenu } from './UserMenu';
import { useWebSocket } from '@/hooks/useWebSocket';
import { cn } from '@/lib/cn';
import type { User } from '@/lib/types';

interface Props {
  user: User;
  onLogout: () => void;
}

const navItems = [
  { to: '/devices',  label: 'Devices',  roles: ['superadmin', 'admin', 'operator', 'viewer'] as const },
  { to: '/history',  label: 'History',  roles: ['superadmin', 'admin', 'operator', 'viewer'] as const },
  { to: '/users',    label: 'Users',    roles: ['superadmin'] as const },
  { to: '/audit',    label: 'Audit',    roles: ['superadmin'] as const },
];

export function TopNav({ user, onLogout }: Props) {
  const { live } = useWebSocket();

  return (
    <header className="bg-ink text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-2 sm:gap-4">
        <NavLink to="/devices" className="flex items-center gap-2.5 group shrink-0">
          <Logo size={32} />
          <span className="hidden sm:inline-block leading-none">
            <span className="block font-extrabold tracking-tight text-white text-base">
              Samvidh<span className="text-sky-400">X</span>
            </span>
            <span className="block uppercase tracking-[0.2em] text-[9px] text-white/60 mt-0.5">Systems</span>
          </span>
        </NavLink>

        {/* min-w-0 + horizontal scroll so four superadmin links never push the
            header wider than a phone screen. */}
        <nav className="flex items-center gap-0.5 sm:gap-1 min-w-0 overflow-x-auto">
          {navItems
            .filter((n) => n.roles.includes(user.role as 'superadmin'))
            .map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  cn(
                    'px-2 sm:px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
                    isActive ? 'bg-white/15 text-white' : 'text-white/70 hover:text-white hover:bg-white/10',
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <span
            title={live ? 'Receiving live updates' : 'Connecting…'}
            className={cn(
              'inline-flex items-center gap-1.5 text-xs text-white/70',
              live && 'text-emerald-300',
            )}
          >
            <span className={cn('w-2 h-2 rounded-full', live ? 'bg-emerald-400 heartbeat' : 'bg-slate-500')} />
            <span className="hidden md:inline">{live ? 'Live' : 'Idle'}</span>
          </span>
          <UserMenu user={user} onLogout={onLogout} />
        </div>
      </div>
    </header>
  );
}
